import syncUserByEmailService from './sync-user-by-email-service';
import * as localStorageService from './local-storage-service';

/**
 * Fetch bookmarks by email using the new getBookmarks1_1 endpoint
 * @param {string} email - User's email address
 * @returns {Promise<Object>} { success, bookmarks, lastSyncTime, userId }
 */
export const getBookmarksByEmail = async (email) => {
  try {
    const functionsUrl = syncUserByEmailService.getFunctionsUrl();
    const response = await fetch(`${functionsUrl}/getBookmarks1_1`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      throw new Error(`Error fetching bookmarks: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Unknown error retrieving bookmarks');
    }

    // If the cloud function returned a userId, store it for reference
    if (data.userId) {
      const userData = localStorageService.getUserData();
      if (userData && userData.id !== data.userId) {
        console.log(`Server returned different userId: ${data.userId}`);
        
        // Update the stored user data with the server-side userId
        const updatedUserData = {
          ...userData,
          serverUserId: data.userId
        };
        
        localStorageService.storeUserData(updatedUserData);
      }
    }

    return data;
  } catch (error) {
    console.error('Error in getBookmarksByEmail:', error);
    throw error;
  }
};

/**
 * Fetch full bookmarks list for a user via cloud function
 * This updated version tries to use email-based lookup first if available,
 * falling back to the original userId-based lookup if needed
 * 
 * @param {string} userId
 * @returns {Promise<Object>} { success, bookmarks, lastSyncTime }
 */
export const getBookmarks = async (userId) => {
  try {
    // Try to get the user's email from local storage
    const userData = localStorageService.getUserData();
    const email = userData?.email;
    
    // If we have an email, use the new email-based endpoint
    if (email) {
      console.log('Using email-based bookmarks retrieval');
      return await getBookmarksByEmail(email);
    }
    
    // Fall back to the original userId-based method
    console.log('Falling back to userId-based bookmarks retrieval');
    const functionsUrl = syncUserByEmailService.getFunctionsUrl();
    const url = `${functionsUrl}/getBookmarks?userId=${encodeURIComponent(userId)}`;
    const response = await fetch(url, { method: 'GET' });
    
    if (!response.ok) {
      throw new Error(`Error fetching bookmarks: ${response.statusText}`);
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Unknown error retrieving bookmarks');
    }
    
    return data;
  } catch (error) {
    console.error('Error in getBookmarks:', error);
    throw error;
  }
};
